import type {ProductFilter} from '@shopify/hydrogen/storefront-api-types';
import type {BFSProductType} from 'domains/collections';

/**
 * Native Shopify Storefront API product filtering for PLPs, replacing the
 * Boost Commerce (bc-sf-filter) service. Facets come from Search & Discovery
 * config via collection.products(filters:).filters — including the custom.*
 * metafield facets (helmet type, certifications, pinlock...) Boost never
 * indexed.
 *
 * URL contract:
 *   - `filter.<facet id>` params carry the facet value's `input` JSON verbatim
 *     (e.g. filter.p.vendor={"productVendor":"SHOEI"}); the loader JSON-parses
 *     every `filter.*` param into the ProductFilter list, so the UI never
 *     needs a reverse mapping.
 *   - Availability is special-cased: absent = default in-stock only;
 *     `filter.v.availability=all` = no availability constraint (both states);
 *     otherwise the param is an {"available":bool} input like any other.
 *   - `page` / `sort` keep the exact values the Boost UI used, so pagination
 *     and sorting components work unchanged.
 *
 * Facet-count semantics (verified empirically on this store): each facet is
 * self-excluded — its value counts ignore filters applied on that same facet
 * but include every other applied filter. deriveTotalProducts exploits this:
 * the availability facet's counts are exact totals for the current filter
 * set, which is how we get a total the Storefront API doesn't expose.
 */

export const FILTER_PARAM_PREFIX = 'filter.';
export const AVAILABILITY_FILTER_ID = 'filter.v.availability';
export const PRICE_FILTER_ID = 'filter.v.price';
export const VENDOR_FILTER_ID = 'filter.p.vendor';
export const SIZE_OPTION_FILTER_ID = 'filter.v.option.size';
/** Sentinel value for the availability param meaning "no constraint". */
export const AVAILABILITY_ALL = 'all';
/**
 * Hidden smart collection containing the whole catalog (variant price > -1),
 * published only to the headless storefront channel. It exists so pages that
 * filter across the entire store (vendor/brand PLPs) can use native
 * collection.products(filters:) facets — the Storefront API exposes facets
 * on collections only.
 */
export const ALL_PRODUCTS_COLLECTION_HANDLE = 'all';

/**
 * The store models stock availability as a custom product metafield facet
 * (`custom.stock_status` = 有庫存 / 無庫存), enabled in Search & Discovery —
 * NOT the native Shopify Availability facet, which is disabled here. Because
 * `collection.products(filters:)` silently ignores inputs for disabled facets,
 * the native `{available: true}` filter has no effect on this store; the
 * in-stock default has to be expressed against the enabled metafield facet.
 */
export const STOCK_STATUS_FILTER_ID = 'filter.p.m.custom.stock_status';
export const STOCK_STATUS_METAFIELD_NAMESPACE = 'custom';
export const STOCK_STATUS_METAFIELD_KEY = 'stock_status';
export const STOCK_STATUS_IN_STOCK_VALUE = '有庫存';
export const STOCK_STATUS_OUT_OF_STOCK_VALUE = '無庫存';

/** Build the `input` JSON for a stock_status facet value, matching the shape
 * Shopify echoes back in `filters[].values[].input`. */
export function stockStatusFilterInput(value: string): string {
  return JSON.stringify({
    productMetafield: {
      namespace: STOCK_STATUS_METAFIELD_NAMESPACE,
      key: STOCK_STATUS_METAFIELD_KEY,
      value,
    },
  });
}

/** The default filter applied when the shopper has made no explicit stock
 * choice: show in-stock products only. */
export const STOCK_STATUS_IN_STOCK_FILTER: ProductFilter = {
  productMetafield: {
    namespace: STOCK_STATUS_METAFIELD_NAMESPACE,
    key: STOCK_STATUS_METAFIELD_KEY,
    value: STOCK_STATUS_IN_STOCK_VALUE,
  },
};

export interface NativeFilterValue {
  id: string;
  label: string;
  count: number;
  /** JSON-serialized ProductFilter, ready to echo back into `filters:`. */
  input: string;
}

export interface NativeFilter {
  id: string;
  label: string;
  type: 'LIST' | 'PRICE_RANGE' | 'BOOLEAN';
  values: NativeFilterValue[];
}

export interface NativePriceBounds {
  min: number;
  max: number;
}

export interface NativeFiltersPayload {
  facets: NativeFilter[];
  priceBounds: NativePriceBounds | null;
}

export interface ParsedNativeParams {
  page: number;
  sort: string;
  filters: ProductFilter[];
  /** true when the URL carries an explicit availability choice. */
  hasExplicitAvailability: boolean;
  hasPriceFilter: boolean;
}

export function sortToNative(sort: string | null): {
  sortKey: string;
  reverse: boolean;
} {
  switch (sort) {
    case 'best-selling':
      return {sortKey: 'BEST_SELLING', reverse: false};
    case 'published-descending':
      return {sortKey: 'CREATED', reverse: true};
    case 'price-ascending':
      return {sortKey: 'PRICE', reverse: false};
    case 'price-descending':
      return {sortKey: 'PRICE', reverse: true};
    case 'title-ascending':
      return {sortKey: 'TITLE', reverse: false};
    case 'title-descending':
      return {sortKey: 'TITLE', reverse: true};
    case 'collection-default':
      // Explicit opt-in to the merchandised order set in Shopify admin.
      return {sortKey: 'COLLECTION_DEFAULT', reverse: false};
    default:
      // 預設 (manual / no sort param): newest-listed products first.
      return {sortKey: 'CREATED', reverse: true};
  }
}

function coercePriceInput(raw: unknown): {min?: number; max?: number} | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const p = raw as {min?: unknown; max?: unknown};
  const out: {min?: number; max?: number} = {};
  if (p.min != null) {
    const n = Number(p.min);
    if (!Number.isFinite(n)) return null;
    out.min = n;
  }
  if (p.max != null) {
    const n = Number(p.max);
    if (!Number.isFinite(n)) return null;
    out.max = n;
  }
  if (out.min == null && out.max == null) return null;
  return out;
}

/**
 * Strict shape-check of a parsed filter.* param against the ProductFilter
 * input type. Anything else (arrays, unknown keys, wrong value types) is
 * dropped — the Storefront API rejects invalid variables at coercion and the
 * Hydrogen client throws on GraphQL errors, which would 500 the whole PLP
 * for a mangled URL.
 */
export function sanitizeProductFilter(raw: unknown): ProductFilter | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length !== 1) return null;
  const key = keys[0];
  const value = obj[key];
  switch (key) {
    case 'available':
      return typeof value === 'boolean' ? {available: value} : null;
    case 'price': {
      const price = coercePriceInput(value);
      return price ? {price} : null;
    }
    case 'productVendor':
    case 'productType':
    case 'tag':
      return typeof value === 'string' && value !== ''
        ? ({[key]: value} as ProductFilter)
        : null;
    case 'variantOption': {
      const v = value as {name?: unknown; value?: unknown} | null;
      return v &&
        typeof v === 'object' &&
        !Array.isArray(v) &&
        typeof v.name === 'string' &&
        typeof v.value === 'string'
        ? {variantOption: {name: v.name, value: v.value}}
        : null;
    }
    case 'productMetafield':
    case 'variantMetafield': {
      const v = value as
        | {namespace?: unknown; key?: unknown; value?: unknown}
        | null;
      return v &&
        typeof v === 'object' &&
        !Array.isArray(v) &&
        typeof v.namespace === 'string' &&
        typeof v.key === 'string' &&
        typeof v.value === 'string'
        ? ({
            [key]: {namespace: v.namespace, key: v.key, value: v.value},
          } as unknown as ProductFilter)
        : null;
    }
    default:
      return null;
  }
}

export function parseNativeFilterParams(
  searchParams: URLSearchParams,
  options?: {skipInStockDefault?: boolean},
): ParsedNativeParams {
  const filters: ProductFilter[] = [];
  let hasExplicitAvailability = false;
  let hasExplicitStockStatus = false;

  for (const [key, value] of searchParams.entries()) {
    if (!key.startsWith(FILTER_PARAM_PREFIX)) continue;

    if (key === AVAILABILITY_FILTER_ID) {
      // Only a VALID value counts as an explicit choice — a mangled value
      // must not silently disable the in-stock default.
      if (value === AVAILABILITY_ALL) {
        hasExplicitAvailability = true;
        continue;
      }
      try {
        const sanitized = sanitizeProductFilter(JSON.parse(value));
        if (sanitized && 'available' in sanitized) {
          hasExplicitAvailability = true;
          filters.push(sanitized);
        }
      } catch {
        // malformed param — ignore
      }
      continue;
    }

    try {
      const sanitized = sanitizeProductFilter(JSON.parse(value));
      if (sanitized) {
        filters.push(sanitized);
        if (key === STOCK_STATUS_FILTER_ID) hasExplicitStockStatus = true;
      }
    } catch {
      // malformed param — ignore
    }
  }

  if (
    !options?.skipInStockDefault &&
    !hasExplicitAvailability &&
    !hasExplicitStockStatus
  ) {
    // Site-wide default the Boost setup also enforced: hide out-of-stock.
    // Expressed against the enabled custom.stock_status metafield facet —
    // the native {available:true} filter is silently ignored on this store
    // because its Availability facet is disabled in Search & Discovery.
    // Some collections (e.g. preorder-products) are always out of stock, so
    // callers opt out of this default via skipInStockDefault.
    filters.push(STOCK_STATUS_IN_STOCK_FILTER);
  }

  const rawPage = parseInt(searchParams.get('page') ?? '1', 10);
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
  const sort = searchParams.get('sort') ?? 'manual';

  return {
    page,
    sort,
    filters,
    hasExplicitAvailability,
    hasPriceFilter: filters.some((f) => 'price' in f),
  };
}

/**
 * Old Boost URLs (indexed links, bookmarks) → native filter params.
 * Returns null when the URL has no pf_* params (nothing to redirect).
 * `keep` lists pf_* keys that are part of the route's own URL contract and
 * must pass through untouched (the vendor route's pf_v_brand identity param).
 */
export function legacyBoostParamsToNative(
  searchParams: URLSearchParams,
  options?: {keep?: string[]},
): URLSearchParams | null {
  const keep = options?.keep ?? [];
  const hasLegacy = Array.from(searchParams.keys()).some(
    (k) => k.startsWith('pf_') && !keep.includes(k),
  );
  if (!hasLegacy) return null;

  const next = new URLSearchParams();
  for (const [key, value] of searchParams.entries()) {
    if (keep.includes(key)) {
      next.append(key, value);
      continue;
    }
    if (key === 'pf_v_brand' && value) {
      next.append('filter.p.vendor', JSON.stringify({productVendor: value}));
      continue;
    }
    if (key === 'pf_opt_size' && value) {
      next.append(
        'filter.v.option.size',
        JSON.stringify({variantOption: {name: 'size', value}}),
      );
      continue;
    }
    if (key === 'pf_p_price' && value) {
      const [min, max] = value.split(':');
      const price: {min?: number; max?: number} = {};
      if (min && Number.isFinite(parseFloat(min))) price.min = parseFloat(min);
      if (max && Number.isFinite(parseFloat(max))) price.max = parseFloat(max);
      if (price.min != null || price.max != null) {
        next.append(PRICE_FILTER_ID, JSON.stringify({price}));
      }
      continue;
    }
    if (key === 'pf_st_stock_status') {
      if (value === 'out-of-stock' || value === 'false') {
        next.append(
          STOCK_STATUS_FILTER_ID,
          stockStatusFilterInput(STOCK_STATUS_OUT_OF_STOCK_VALUE),
        );
      }
      // in-stock is the default — omit
      continue;
    }
    if (key.startsWith('pf_')) {
      // Boost tag/style/colour filters have no native facet — drop
      continue;
    }
    next.append(key, value);
  }
  return next;
}

/**
 * Exact result total for the current filter set, derived from the
 * self-excluded availability facet. Returns null when the facet is missing
 * (caller should fall back to countProductsByScan).
 */
export function deriveTotalProducts(
  facets: NativeFilter[],
  filters: ProductFilter[],
): number | null {
  const availability = facets.find((f) => f.id === AVAILABILITY_FILTER_ID);
  if (!availability || !Array.isArray(availability.values)) return null;

  const appliedAvailability = filters
    .map((f) => (f as {available?: boolean}).available)
    .filter((v): v is boolean => typeof v === 'boolean');

  let total = 0;
  for (const value of availability.values) {
    try {
      const input = JSON.parse(value.input) as {available?: boolean};
      if (typeof input?.available !== 'boolean') return null;
      if (
        appliedAvailability.length === 0 ||
        appliedAvailability.includes(input.available)
      ) {
        total += value.count ?? 0;
      }
    } catch {
      return null;
    }
  }
  return total;
}

interface StorefrontQueryArgs {
  storefront: {
    query: <T>(query: string, options: any) => Promise<T>;
  };
  handle: string;
  filters: ProductFilter[];
  sortKey: string;
  reverse: boolean;
  country: string;
  language: string;
}

interface ScanConnection {
  collection: {
    products: {
      pageInfo: {hasNextPage: boolean};
      edges: {cursor: string}[];
    };
  } | null;
}

const SCAN_HOP = 250;

/**
 * The Storefront API only paginates by cursor, but the PLP URL contract is
 * numbered pages. Resolve the `after` cursor for a numeric offset by hopping
 * through cursor-only pages. Hops are always a full SCAN_HOP with identical
 * variables regardless of the target page, so every page 2..7 of a
 * 40-per-page PLP shares one cached first-hop query instead of each shape
 * missing the cache. outOfRange=true when the offset is past the end.
 */
export async function resolveOffsetCursor(
  args: StorefrontQueryArgs & {offset: number},
): Promise<{after: string | null; outOfRange: boolean}> {
  const {storefront, offset, ...vars} = args;
  if (offset <= 0) return {after: null, outOfRange: false};

  let hopAfter: string | null = null;
  let consumed = 0;
  for (;;) {
    const data: ScanConnection = await storefront.query(
      NATIVE_CURSOR_SCAN_QUERY,
      {variables: {...vars, first: SCAN_HOP, after: hopAfter}},
    );
    const edges = data?.collection?.products?.edges ?? [];
    if (offset <= consumed + edges.length) {
      return {after: edges[offset - consumed - 1].cursor, outOfRange: false};
    }
    if (edges.length < SCAN_HOP) {
      return {after: null, outOfRange: true};
    }
    consumed += edges.length;
    hopAfter = edges[edges.length - 1].cursor;
  }
}

/**
 * Fallback total when the availability facet is unavailable: walk the cursor
 * pages counting edges. Capped — a PLP total beyond the cap just reads as the
 * cap, which only affects the last pagination links of huge collections.
 */
export async function countProductsByScan(
  args: StorefrontQueryArgs & {cap?: number},
): Promise<number> {
  const {storefront, cap = 2000, ...vars} = args;
  let after: string | null = null;
  let count = 0;
  while (count < cap) {
    const data: ScanConnection = await storefront.query(
      NATIVE_CURSOR_SCAN_QUERY,
      {variables: {...vars, first: SCAN_HOP, after}},
    );
    const conn = data?.collection?.products;
    const edges = conn?.edges ?? [];
    count += edges.length;
    if (!conn?.pageInfo?.hasNextPage || edges.length === 0) break;
    after = edges[edges.length - 1].cursor;
  }
  return count;
}

/**
 * A sidebar row that may stand for several raw facet values. Normal facets
 * map 1:1; the size facet merges synonymous product conventions ("2XL" vs
 * "XXL", "M [59-60cm]" vs "M") into one row whose inputs are OR'd by the
 * Storefront API when applied together.
 */
export interface DisplayFacetValue {
  key: string;
  label: string;
  count: number;
  inputs: string[];
}

const SIZE_SYNONYMS: Record<string, string> = {
  '2XS': 'XXS',
  '3XS': 'XXXS',
  '2XL': 'XXL',
  '3XL': 'XXXL',
  '4XL': 'XXXXL',
};

const SIZE_LADDER = [
  'XXXS',
  'XXS',
  'XS',
  'S',
  'M',
  'L',
  'XL',
  'XXL',
  'XXXL',
  'XXXXL',
  'FREE',
  'FREE SIZE',
  'ONE SIZE',
];

export function canonicalSizeKey(label: string): string {
  const stripped = label
    .trim()
    .toUpperCase()
    // drop measurement suffixes like "M [59-60CM]" / "S (57-58)"
    .replace(/\s*[\[(][^\])]*[\])]/g, '')
    .trim();
  return SIZE_SYNONYMS[stripped] ?? stripped;
}

export function mergeSizeValues(
  values: NativeFilterValue[],
): DisplayFacetValue[] {
  const byKey = new Map<string, DisplayFacetValue>();
  for (const v of values) {
    const key = canonicalSizeKey(v.label);
    const existing = byKey.get(key);
    if (existing) {
      existing.count += v.count ?? 0;
      existing.inputs.push(v.input);
    } else {
      byKey.set(key, {
        key,
        label: key,
        count: v.count ?? 0,
        inputs: [v.input],
      });
    }
  }
  return [...byKey.values()].sort((a, b) => {
    const ai = SIZE_LADDER.indexOf(a.key);
    const bi = SIZE_LADDER.indexOf(b.key);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.key.localeCompare(b.key, undefined, {numeric: true});
  });
}

export function extractPriceBounds(
  facets: NativeFilter[],
): NativePriceBounds | null {
  const price = facets.find((f) => f.id === PRICE_FILTER_ID);
  const input = price?.values?.[0]?.input;
  if (!input) return null;
  try {
    const parsed = JSON.parse(input) as {price?: {min?: number; max?: number}};
    if (parsed?.price?.min == null || parsed?.price?.max == null) return null;
    return {min: parsed.price.min, max: parsed.price.max};
  } catch {
    return null;
  }
}

export interface NativeProductNode {
  id: string;
  title: string;
  handle: string;
  availableForSale: boolean;
  publishedAt: string;
  vendor: string;
  tags: string[];
  options: {name: string; values: string[]}[];
  featuredImage: {
    id: string | null;
    url: string;
    width: number;
    height: number;
  } | null;
  variants: {
    nodes: {
      id: string;
      title: string;
      availableForSale: boolean;
      image: {url: string} | null;
      price: {amount: string; currencyCode: string};
      compareAtPrice: {amount: string; currencyCode: string} | null;
      selectedOptions: {name: string; value: string}[];
    }[];
  };
}

const gidToId = (gid: string) => gid.substring(gid.lastIndexOf('/') + 1);

/**
 * Storefront MoneyV2 amounts serialize with minimal decimals ("2580.0");
 * Boost supplied two-decimal strings and one ProductPrices branch renders the
 * raw amount, so normalize at the adapter boundary.
 */
const toMoneyString = (amount: string | null | undefined): string | null =>
  amount == null ? null : Number(amount).toFixed(2);

/**
 * Adapt a native product node to the slim Boost (BFS) product shape that
 * CollectionProducts/transformProducts already consumes — the whole product
 * grid, quick-add and currency conversion work unchanged. price_twd is left
 * unset on purpose: the store sells in HKD only (all Shopify Markets are
 * HKD); the TW storefront's default selectedCurrency=TWD makes the client
 * convert via the Sanity-managed exchange rate, same as every other non-HKD
 * currency.
 */
export function nativeProductToBfs(node: NativeProductNode): BFSProductType {
  return {
    id: gidToId(node.id) as unknown as number,
    title: node.title,
    handle: node.handle,
    available: node.availableForSale,
    published_at: node.publishedAt,
    tags: node.tags ?? [],
    vendor: node.vendor,
    images_info: node.featuredImage
      ? [
          {
            src: node.featuredImage.url,
            id: node.featuredImage.id as unknown as number,
            height: node.featuredImage.height,
            width: node.featuredImage.width,
          },
        ]
      : [],
    options_with_values: (node.options ?? []).map((o) => ({
      label: o.name,
      name: o.name,
      values: (o.values ?? []).map((v) => ({image: 0, title: v})),
    })),
    variants: (node.variants?.nodes ?? []).map((v) => {
      const price = toMoneyString(v.price?.amount);
      const compareAt = toMoneyString(v.compareAtPrice?.amount);
      return {
        id: gidToId(v.id),
        title: v.title,
        available: v.availableForSale,
        image: v.image?.url ?? null,
        price,
        price_hkd: v.price?.currencyCode === 'HKD' ? price : undefined,
        price_twd: v.price?.currencyCode === 'TWD' ? price : undefined,
        compare_at_price: compareAt,
        compare_at_price_hkd:
          v.compareAtPrice?.currencyCode === 'HKD' ? compareAt : undefined,
        compare_at_price_twd:
          v.compareAtPrice?.currencyCode === 'TWD' ? compareAt : undefined,
        merged_options: (v.selectedOptions ?? []).map(
          (o) => `${o.name}:${o.value}`,
        ),
      };
    }),
  };
}

export const NATIVE_COLLECTION_PAGE_QUERY = `#graphql
  query NativeCollectionPage(
    $handle: String!
    $filters: [ProductFilter!]
    $sortKey: ProductCollectionSortKeys
    $reverse: Boolean
    $first: Int
    $after: String
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      id
      products(
        first: $first
        after: $after
        filters: $filters
        sortKey: $sortKey
        reverse: $reverse
      ) {
        filters {
          id
          label
          type
          values {
            id
            label
            count
            input
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          title
          handle
          availableForSale
          publishedAt
          vendor
          tags
          options {
            name
            values
          }
          featuredImage {
            id
            url
            width
            height
          }
          variants(first: 100) {
            nodes {
              id
              title
              availableForSale
              image {
                url
              }
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
                currencyCode
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Facets only — used to recover the full price range when a price filter is
 * applied (the price facet echoes the applied values back instead of the
 * unfiltered range, so slider bounds need a query without the price filter).
 */
export const NATIVE_FACETS_QUERY = `#graphql
  query NativeCollectionFacets(
    $handle: String!
    $filters: [ProductFilter!]
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      products(first: 1, filters: $filters) {
        filters {
          id
          label
          type
          values {
            id
            label
            count
            input
          }
        }
      }
    }
  }
`;

export const NATIVE_CURSOR_SCAN_QUERY = `#graphql
  query NativeCollectionCursorScan(
    $handle: String!
    $filters: [ProductFilter!]
    $sortKey: ProductCollectionSortKeys
    $reverse: Boolean
    $first: Int
    $after: String
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      products(
        first: $first
        after: $after
        filters: $filters
        sortKey: $sortKey
        reverse: $reverse
      ) {
        pageInfo {
          hasNextPage
        }
        edges {
          cursor
        }
      }
    }
  }
`;

const VENDOR_SCAN_QUERY = `#graphql
  query VendorScan(
    $handle: String!
    $filters: [ProductFilter!]
    $first: Int
    $after: String
  ) {
    collection(handle: $handle) {
      products(first: $first, after: $after, filters: $filters) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          vendor
        }
      }
    }
  }
`;

interface VendorScanData {
  collection?: {
    products?: {
      pageInfo?: {hasNextPage: boolean; endCursor: string | null};
      nodes?: {vendor?: string | null}[];
    } | null;
  } | null;
}

/**
 * Every distinct vendor with at least one in-stock product, straight from
 * Shopify. The /pages/brands list used to come from Sanity's hand-maintained
 * brand documents, which drifted: brands with stock were missing, brands
 * with nothing to sell stayed listed.
 *
 * The vendor facet cannot do this — the Storefront API caps a filter's
 * values at 100 and the store has ~170 in-stock vendors — so this walks the
 * hidden all-products collection under the same in-stock metafield gate the
 * PLPs use (~6 cached round-trips at 250/page). Same filter, same
 * definition of "in stock": a listed brand can never land on an empty PLP.
 */
export async function fetchInStockVendors(
  storefront: StorefrontQueryArgs['storefront'] & {CacheLong: () => unknown},
): Promise<string[]> {
  const vendors = new Set<string>();
  let after: string | null = null;
  for (let i = 0; i < 40; i++) {
    const data: VendorScanData = await storefront.query(VENDOR_SCAN_QUERY, {
      variables: {
        handle: ALL_PRODUCTS_COLLECTION_HANDLE,
        filters: [STOCK_STATUS_IN_STOCK_FILTER],
        first: 250,
        after,
      },
      cache: storefront.CacheLong(),
    });
    const conn = data?.collection?.products;
    for (const node of conn?.nodes ?? []) {
      const vendor = node?.vendor?.trim();
      if (vendor) vendors.add(vendor);
    }
    if (!conn?.pageInfo?.hasNextPage || !conn.pageInfo.endCursor) break;
    after = conn.pageInfo.endCursor;
  }
  return [...vendors].sort((a, b) =>
    a.localeCompare(b, 'en', {sensitivity: 'base'}),
  );
}

export const PRODUCT_TYPE_FACET_ID = 'filter.p.product_type';

export interface TypeGroupedWindow {
  /** Nodes for the requested window, in group order. */
  nodes: unknown[];
  /** Facets from the grouping query — same shape the page query returns. */
  facets: NativeFilter[];
  totalProducts: number;
  outOfRange: boolean;
}

/**
 * The default PLP ordering: group by product type first, newest-listed first
 * within each type. A page that spans one type keeps reading as a plain
 * newest-first list; a mixed page (a brand, 騎士裝備, all-products) stops
 * interleaving helmets with gloves and oil.
 *
 * The Storefront API has no product-type sort key, so this fakes one with
 * facet arithmetic: the product_type facet gives every type's count under
 * the current filters, which makes the grouped list addressable — cumulative
 * counts locate any page window, and each window touches at most a couple of
 * type buckets, each fetched with its own productType filter sorted
 * CREATED-desc. A page view costs one facet query plus one or two bucket
 * queries — not a walk of the whole collection.
 *
 * Returns null when grouping adds nothing (zero or one type in play, or no
 * usable facet) — the caller falls back to the plain query path.
 */
export async function fetchTypeGroupedWindow(args: {
  storefront: StorefrontQueryArgs['storefront'];
  handle: string;
  filters: ProductFilter[];
  offset: number;
  first: number;
  country: string;
  language: string;
}): Promise<TypeGroupedWindow | null> {
  const {storefront, handle, filters, offset, first, country, language} = args;

  const facetsData = await storefront.query<{
    collection?: {products?: {filters?: NativeFilter[]}};
  }>(NATIVE_FACETS_QUERY, {
    variables: {handle, filters, country, language},
  });
  const facets = facetsData?.collection?.products?.filters ?? [];
  const typeFacet = facets.find((f) => f.id === PRODUCT_TYPE_FACET_ID);
  if (!typeFacet || !Array.isArray(typeFacet.values)) return null;

  // A facet reports its own values as if that facet were not applied, so an
  // applied product-type filter narrows the buckets here, not in the counts.
  const appliedTypes = filters
    .map((f) => (f as {productType?: string}).productType)
    .filter((t): t is string => typeof t === 'string');

  const buckets: {type: string; count: number}[] = [];
  for (const value of typeFacet.values) {
    let type: string | undefined;
    try {
      type = (JSON.parse(value.input) as {productType?: string}).productType;
    } catch {
      return null;
    }
    if (!type || typeof value.count !== 'number') continue;
    if (value.count <= 0) continue;
    if (appliedTypes.length > 0 && !appliedTypes.includes(type)) continue;
    buckets.push({type, count: value.count});
  }
  if (buckets.length <= 1) return null;

  const totalProducts = buckets.reduce((sum, b) => sum + b.count, 0);
  if (offset >= totalProducts) {
    return {nodes: [], facets, totalProducts, outOfRange: true};
  }

  // Bucket queries re-apply the type themselves; strip any applied type
  // filter so the two never conflict.
  const baseFilters = filters.filter(
    (f) => !(f as {productType?: string}).productType,
  );

  const nodes: unknown[] = [];
  let cursor = 0;
  for (const bucket of buckets) {
    if (nodes.length >= first) break;
    const bucketStart = cursor;
    cursor += bucket.count;
    if (offset >= cursor) continue;

    // Only the first bucket in the window starts mid-bucket; every later
    // bucket starts from its own newest item.
    const intraOffset = Math.max(0, offset - bucketStart);
    const take = Math.min(bucket.count - intraOffset, first - nodes.length);
    if (take <= 0) continue;

    const bucketFilters = [...baseFilters, {productType: bucket.type}];
    const {after, outOfRange} = await resolveOffsetCursor({
      storefront,
      handle,
      filters: bucketFilters,
      sortKey: 'CREATED',
      reverse: true,
      country,
      language,
      offset: intraOffset,
    });
    // The facet count said this bucket reaches intraOffset; if the walk
    // disagrees (index lag), skip the bucket rather than serving a hole.
    if (outOfRange) continue;

    const pageData = await storefront.query<{
      collection?: {products?: {nodes?: unknown[]}};
    }>(NATIVE_COLLECTION_PAGE_QUERY, {
      variables: {
        handle,
        filters: bucketFilters,
        sortKey: 'CREATED',
        reverse: true,
        first: take,
        after,
        country,
        language,
      },
    });
    nodes.push(...(pageData?.collection?.products?.nodes ?? []));
  }

  return {nodes, facets, totalProducts, outOfRange: false};
}
